import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Link as MuiLink,
} from '@mui/material';
import {
  Warning as WarningIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

import FileUploadZone from '../components/content/FileUploadZone';
import {
  AIEnrichmentStrip,
  UploadFileList,
  UploadFileRow,
  StatusMenu,
  AddTagsPopover,
  type ContentStatus,
} from '../components/content/upload';
import type { IllustrationFileType } from '../components/content/DocumentIllustration';
import { ledeSx } from '../theme/styleHelpers';
import {
  readUploadAiDefaults,
  writeUploadAiDefaults,
  type UploadAiDefaults,
} from '../utils/uploadAiDefaults';
import { UploadProgress } from '../services/uploadService';
import { Content } from '../types/content.types';
import { useOrganization } from '../context/OrganizationContext';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../utils/permissions';
import useTeamOverview from '../hooks/useTeamOverview';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../services';
import type { UnifiedTag } from '../services/content/tag.service';

// Pure helper — no closure needed, defined once at module scope.
const resolveFileType = (file: UploadProgress): IllustrationFileType => {
  const name = (file.fileName ?? '').toLowerCase();
  if (/\.(zip|tar|tar\.gz|tgz)$/.test(name)) return 'archive';
  if (/\.(mp3|wav|flac|aac|ogg|m4a)$/.test(name)) return 'audio';
  if (/\.(mp4|mov|avi|mkv|webm|wmv)$/.test(name)) return 'video';
  if (/\.(jpg|jpeg|png|gif|svg|webp|bmp|tiff?)$/.test(name)) return 'img';
  if (name.endsWith('.pdf')) return 'pdf';
  if (/\.pptx?$/.test(name)) return 'ppt';
  if (/\.html?$/.test(name)) return 'html';
  return 'doc';
};

const ContentUpload: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState<UploadProgress[]>([]);
  const [contentData, setContentData] = useState<Partial<Content>[]>([]);
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());

  // Anchor element for whichever status menu / tag popover is currently open.
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(null);
  const [tagPopoverAnchor, setTagPopoverAnchor] = useState<HTMLElement | null>(null);
  // Targets: either a specific file index (for per-row), or 'bulk' (for bulk action).
  const [statusMenuTarget, setStatusMenuTarget] = useState<number | 'bulk' | null>(null);
  const [tagPopoverTarget, setTagPopoverTarget] = useState<number | 'bulk' | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    filesWithMissingDescriptions: Array<{ index: number; fileName: string }>;
    onConfirm: () => void;
  }>({
    open: false,
    filesWithMissingDescriptions: [],
    onConfirm: () => {},
  });

  const [availableTags, setAvailableTags] = useState<UnifiedTag[]>([]);
  const [aiDefaults, setAiDefaults] = useState<UploadAiDefaults>(() => readUploadAiDefaults());

  const handleAiDefaultsChange = (next: UploadAiDefaults) => {
    setAiDefaults(next);
    writeUploadAiDefaults(next);
  };

  // Aliases preserved so downstream JSX (post-upload form props) compiles
  // until Pass 3 replaces those call sites.
  const useAiTitle = aiDefaults.aiTitle;
  const useAiDescription = aiDefaults.aiDescription;
  const aiApplyTags = aiDefaults.aiTags;
  const aiTagMode: 'off' | 'merge' = aiApplyTags ? 'merge' : 'off';

  // Content affirmation states
  const [affirmationStatus, setAffirmationStatus] = useState<any>(null);
  const [affirmationLoading, setAffirmationLoading] = useState(true);
  const [showAffirmationDialog, setShowAffirmationDialog] = useState(false);
  const [affirmingContent, setAffirmingContent] = useState(false);

  const { currentOrganizationId, currentTeamId, organizations, teams, refreshData } = useOrganization();
  const { user } = useAuth();
  const { refreshOverview } = useTeamOverview(currentTeamId);

  // Check permissions
  const { hasPermission } = usePermissions();
  const canCreateContent = hasPermission(PERMISSIONS.CONTENT_CREATE);

  // Get current organization and team context
  const currentOrg = organizations.find(org => org.id === currentOrganizationId);
  const currentTeam = teams.find(team => team.id === currentTeamId);

  const teamContext = {
    organizationId: currentOrganizationId || '',
    organizationName: currentOrg?.name || '',
    teamId: currentTeamId || undefined,
    teamName: currentTeam?.name || undefined,
  };

  // Load content affirmation status on mount
  useEffect(() => {
    const checkAffirmationStatus = async () => {
      try {
        setAffirmationLoading(true);
        const status = await userService.getContentAffirmationStatus();
        setAffirmationStatus(status);

        // If reaffirmation required, show dialog immediately
        if (status.requiresReaffirmation) {
          setShowAffirmationDialog(true);
        }
      } catch (error) {
        console.error('Failed to check affirmation status:', error);
        setSnackbar({
          open: true,
          message: 'Failed to check content affirmation status',
          severity: 'warning',
        });
      } finally {
        setAffirmationLoading(false);
      }
    };

    if (user) {
      checkAffirmationStatus();
    }
  }, [user]);

  // Load available team tags once when org + team are known
  useEffect(() => {
    if (!currentOrganizationId || !currentTeamId) return;
    let cancelled = false;
    tagService
      .listUnifiedTags({
        organization_id: currentOrganizationId,
        team_id: currentTeamId,
        tag_type: 'organizational',
        limit: 100,
      })
      .then((tags) => {
        if (!cancelled) setAvailableTags(tags);
      })
      .catch((err) => {
        console.error('Failed to load team tags for upload', err);
      });
    return () => { cancelled = true; };
  }, [currentOrganizationId, currentTeamId]);

  // Handle content affirmation
  const handleAffirmContent = async () => {
    setAffirmingContent(true);

    try {
      const currentTermsVersion = '2025-01-17'; // TODO: Get from config
      await userService.affirmContentResponsibility(currentTermsVersion);

      // Reload status
      const status = await userService.getContentAffirmationStatus();
      setAffirmationStatus(status);
      setShowAffirmationDialog(false);

      setSnackbar({
        open: true,
        message: 'Content upload responsibility affirmed. You can now proceed with uploading.',
        severity: 'success',
      });
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Failed to affirm content responsibility',
        severity: 'error',
      });
    } finally {
      setAffirmingContent(false);
    }
  };

  // Handle file upload completion
  const handleUploadComplete = async (files: UploadProgress[]) => {
    setUploadedFiles(files);

    // Initialize content data for each file with status numeric value
    const initialContentData = files.map(file => ({
      id: file.contentId || '', // Store content ID for later updates
      title: file.fileName.replace(/\.[^/.]+$/, ''), // Remove file extension
      description: '',
      tags: [],
      status: 'draft' as const,
    }));

    setContentData(initialContentData);
    setActiveStep(1);
    setSnackbar({
      open: true,
      message: `${files.length} file${files.length > 1 ? 's' : ''} uploaded successfully!`,
      severity: 'success',
    });

    // Note: We skip the expensive refreshData() call here because:
    // 1. It reloads ALL orgs, teams, members, subscriptions, and invitations
    // 2. The content count will be refreshed when user navigates to library
    // 3. Storage statistics don't need immediate update (eventual consistency is fine)
    console.log('Refreshing team overview statistics after content upload...');
    try {
      await refreshOverview();   // Only refresh team overview statistics (lightweight)
      console.log('Team overview refreshed successfully after upload');
    } catch (refreshError) {
      console.warn('Failed to refresh team overview after upload:', refreshError);
      // Don't fail upload if refresh fails - user can manually refresh
    }
  };

  // Handle batch update
  const handleBatchUpdate = (index: number, data: Partial<Content>) => {
    setContentData(prev => {
      const updated = [...prev];
      updated[index] = data;
      return updated;
    });
  };

  // Handle batch save all
  const handleBatchSaveAll = async () => {
    try {
      const validationErrors: string[] = [];
      const filesWithMissingDescriptions: Array<{ index: number; fileName: string }> = [];

      // Validate all files first
      uploadedFiles.forEach((file, index) => {
        const metadata = contentData[index];

        // Title is required unless AI is providing it
        if (!useAiTitle && !metadata.title?.trim()) {
          validationErrors.push(`${file.fileName}: missing title`);
        }

        // Track files with missing descriptions (optional but we want to warn; skip if AI is providing it)
        if (!useAiDescription && !metadata.description?.trim()) {
          filesWithMissingDescriptions.push({ index, fileName: file.fileName });
        }
      });

      // Show validation errors if any required fields are missing
      if (validationErrors.length > 0) {
        const errorMessage = `Please fill in required fields:\n${validationErrors.slice(0, 3).join('\n')}${validationErrors.length > 3 ? `\n...and ${validationErrors.length - 3} more` : ''}`;
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
        });
        return;
      }

      // If there are files with missing descriptions, show confirmation dialog
      if (filesWithMissingDescriptions.length > 0) {
        setConfirmDialog({
          open: true,
          filesWithMissingDescriptions,
          onConfirm: () => {
            setConfirmDialog(prev => ({ ...prev, open: false }));
            performBatchSaveAll();
          },
        });
        return;
      }

      // If all descriptions are present, proceed directly
      await performBatchSaveAll();
    } catch (error) {
      console.error('Failed to save batch metadata:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save metadata. Please try again.',
        severity: 'error',
      });
    }
  };

  // Extracted logic for actually performing batch save
  const performBatchSaveAll = async () => {
    try {
      let successCount = 0;
      const updateErrors: string[] = [];

      // Update all files in parallel
      const updates = uploadedFiles.map(async (file, index) => {
        const metadata = contentData[index];
        if (file.contentId && (useAiTitle || metadata.title?.trim())) {
          try {
            const updateData: any = {
              status: metadata.status || 'draft',
            };

            // Only include title if user is providing it (not AI)
            if (!useAiTitle && metadata.title?.trim()) {
              updateData.title = metadata.title.trim();
            }

            // Only include description if user is providing it and it was supplied
            if (!useAiDescription && metadata.description?.trim()) {
              updateData.description = metadata.description.trim();
            }

            await contentService.updateContent(file.contentId, updateData);

            // Apply user-supplied tags additively via the dedicated tag endpoint, so
            // they coexist with any AI-applied tags (auto-promoted server-side when
            // aiTagMode is 'merge') rather than replacing them like updateContent does.
            const userTagIds: string[] = (metadata as any).tag_ids ?? metadata.tags ?? [];
            if (userTagIds.length > 0 && currentTeamId) {
              try {
                await contentService.applyTagsToContent(file.contentId, userTagIds, currentTeamId);
              } catch (tagError: any) {
                console.error(`Failed to apply user tags to content ${file.contentId}:`, tagError);
                const errorMsg = tagError?.response?.data?.message || tagError?.message || 'Unknown error';
                updateErrors.push(`${file.fileName}: tags — ${errorMsg}`);
              }
            }

            successCount++;
          } catch (error: any) {
            console.error(`Failed to update content ${file.contentId}:`, error);
            const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error';
            updateErrors.push(`${file.fileName}: ${errorMsg}`);
          }
        }
      });

      await Promise.all(updates);

      if (updateErrors.length === 0) {
        setSnackbar({
          open: true,
          message: `Successfully saved ${successCount} content item${successCount > 1 ? 's' : ''}!`,
          severity: 'success',
        });

        // Refresh team statistics after batch save
        console.log('Refreshing team overview after batch save...');
        try {
          await refreshOverview();   // Only refresh team overview statistics (lightweight)
          console.log('Team overview refreshed successfully after batch save');
        } catch (refreshError) {
          console.warn('Failed to refresh team overview after batch save:', refreshError);
        }

        // Reset form after successful save
        setTimeout(() => {
          setActiveStep(0);
          setUploadedFiles([]);
          setContentData([]);
          setSelectedFileIds(new Set());
        }, 2000);
      } else {
        const errorMessage = `Saved ${successCount} items, but ${updateErrors.length} failed:\n${updateErrors.slice(0, 3).join('\n')}${updateErrors.length > 3 ? `\n...and ${updateErrors.length - 3} more` : ''}`;
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'warning',
        });
      }
    } catch (error) {
      console.error('Failed to save batch metadata:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save metadata. Please try again.',
        severity: 'error',
      });
    }
  };

  // ---- New file-list handlers ----

  // All entries in `uploadedFiles` have a backend-assigned `contentId` by the time
  // they reach this page (see `handleUploadComplete`). The `String(idx)` fallback
  // is purely defensive — if a file ever arrives without a contentId, selection
  // stability would break on row removal because identities shift with array
  // position. Audit upstream if you ever see "String(idx)" reach the user.
  const fileIdOf = (idx: number): string =>
    uploadedFiles[idx]?.contentId || String(idx);

  const handleToggleSelectAll = () => {
    setSelectedFileIds((prev) => {
      if (prev.size === uploadedFiles.length) return new Set();
      return new Set(uploadedFiles.map((_, i) => fileIdOf(i)));
    });
  };

  const handleToggleSelect = (id: string) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleRowStatusChange = (idx: number, status: ContentStatus) => {
    const existing = contentData[idx] ?? {};
    handleBatchUpdate(idx, { ...existing, status });
  };

  const handleRowTagsAdd = (idx: number, tagsToAdd: string[]) => {
    const existing = contentData[idx] ?? {};
    const existingTags = existing.tags ?? [];
    const merged = Array.from(new Set([...existingTags, ...tagsToAdd]));
    handleBatchUpdate(idx, { ...existing, tags: merged });
  };

  const handleRowRemove = (idx: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== idx));
    setContentData((prev) => prev.filter((_, i) => i !== idx));
    setSelectedFileIds((prev) => {
      const removedId = fileIdOf(idx);
      if (!prev.has(removedId)) return prev;
      const next = new Set(prev);
      next.delete(removedId);
      return next;
    });
  };

  const bulkApply = (mutator: (idx: number) => void) => {
    uploadedFiles.forEach((_, i) => {
      if (selectedFileIds.has(fileIdOf(i))) mutator(i);
    });
  };

  const handleBulkSetStatus = (status: ContentStatus) =>
    bulkApply((i) => handleRowStatusChange(i, status));

  const handleBulkAddTags = (tagsToAdd: string[]) =>
    bulkApply((i) => handleRowTagsAdd(i, tagsToAdd));

  const handleBulkRemove = () => {
    const keptIndices = uploadedFiles
      .map((_, i) => i)
      .filter((i) => !selectedFileIds.has(fileIdOf(i)));
    setUploadedFiles((prev) => keptIndices.map((i) => prev[i]));
    setContentData((prev) => keptIndices.map((i) => prev[i]));
    setSelectedFileIds(new Set());
  };

  // Per-row validation: rows that need a title supplied by the user
  const rowsNeedingTitle = uploadedFiles
    .map((file, idx) => ({
      idx,
      needsTitle: !aiDefaults.aiTitle && !(contentData[idx]?.title?.trim()),
      fileName: file.fileName,
    }))
    .filter((r) => r.needsTitle);

  const isSaveBlocked = rowsNeedingTitle.length > 0;

  // If user doesn't have permission to create content, show error
  if (!canCreateContent) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Permission Required
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            You do not have permission to upload content. The <strong>content:create</strong> permission is required for this operation.
          </Typography>
          <Button variant="contained" onClick={() => window.history.back()}>
            Go Back
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Above-card header: title + lede */}
      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h1"
          sx={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.15, mb: 0.5 }}
        >
          Upload Content
        </Typography>
        <Typography sx={[ledeSx, { mb: 0 }]}>
          Add new content to your team&apos;s library.
        </Typography>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          height: { xs: 'calc(100vh - 220px)', md: 'min(720px, calc(100vh - 240px))' },
          minHeight: 480,
          borderRadius: 3.5,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 1px 2px rgba(15,23,42,.04), 0 4px 14px rgba(15,23,42,.05)',
        }}
      >
        {/* Card Header (fixed): crumb + AI strip — only rendered when it has content */}
        {((activeStep === 0 && !!currentTeamId) || activeStep === 1) && (
          <Box
            sx={{
              p: 3, pt: 2.5, pb: 2,
              borderBottom: 1, borderColor: 'divider',
              flexShrink: 0,
              bgcolor: 'background.paper',
            }}
          >
            {activeStep === 0 && currentTeamId && (
              <>
                {/* Inline breadcrumb */}
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, color: 'text.secondary', fontSize: 12.5, mb: 1.5 }}>
                  Uploading to
                  <Box component="span" sx={{ background: 'rgba(15,23,42,.05)', px: 1.25, py: 0.25, borderRadius: 999, color: 'text.primary', fontWeight: 600 }}>
                    {teamContext.teamName}
                  </Box>
                  in
                  <Box component="span" sx={{ background: 'rgba(15,23,42,.05)', px: 1.25, py: 0.25, borderRadius: 999, color: 'text.primary', fontWeight: 600 }}>
                    {teamContext.organizationName}
                  </Box>
                </Box>

                {/* AI enrichment strip (only when affirmation not required) */}
                {!affirmationLoading && !affirmationStatus?.requiresReaffirmation && (
                  <AIEnrichmentStrip
                    defaults={aiDefaults}
                    onChange={handleAiDefaultsChange}
                  />
                )}
              </>
            )}

            {activeStep === 1 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {/* Inline breadcrumb for metadata step */}
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, color: 'text.secondary', fontSize: 12.5 }}>
                  Uploading to
                  <Box component="span" sx={{ background: 'rgba(15,23,42,.05)', px: 1.25, py: 0.25, borderRadius: 999, color: 'text.primary', fontWeight: 600 }}>
                    {teamContext.teamName}
                  </Box>
                  in
                  <Box component="span" sx={{ background: 'rgba(15,23,42,.05)', px: 1.25, py: 0.25, borderRadius: 999, color: 'text.primary', fontWeight: 600 }}>
                    {teamContext.organizationName}
                  </Box>
                </Box>
                {/* AI strip in read-only chip mode post-upload */}
                <AIEnrichmentStrip
                  defaults={aiDefaults}
                  readOnly={true}
                  status={`${uploadedFiles.length} file${uploadedFiles.length !== 1 ? 's' : ''} processing…`}
                />
              </Box>
            )}
          </Box>
        )}

        {/* Card Body (scrollable) */}
        <Box sx={{ flex: 1, overflowY: 'auto', bgcolor: 'background.default', p: 3 }}>
          {/* Step 1: File Upload */}
          {activeStep === 0 && (
            <Box>
              {!currentTeamId ? (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Team Selection Required
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Please select a team before uploading content. All content must belong to a specific team within your organization.
                  </Typography>
                  <Typography variant="body2">
                    You can select a team from the organization sidebar or create a new team if you have the appropriate permissions.
                  </Typography>
                </Alert>
              ) : (
                <>
                  {/* Show affirmation warning if required */}
                  {affirmationLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                      <CircularProgress />
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                        Checking content upload affirmation...
                      </Typography>
                    </Box>
                  ) : affirmationStatus?.requiresReaffirmation ? (
                    <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningIcon />}>
                      <Typography variant="h6" gutterBottom>
                        Affirmation Required
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        {affirmationStatus.reason === 'never_affirmed' && 'You must affirm content upload responsibility before uploading content.'}
                        {affirmationStatus.reason === 'expired' && 'Your content affirmation has expired (>90 days). Please reaffirm to continue.'}
                        {affirmationStatus.reason === 'terms_updated' && 'Our Terms of Service have been updated. Please review and reaffirm.'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button
                          variant="contained"
                          onClick={() => setShowAffirmationDialog(true)}
                        >
                          Review and Affirm
                        </Button>
                        <Button
                          variant="text"
                          component={Link}
                          to="/profile"
                        >
                          Go to Profile
                        </Button>
                      </Box>
                    </Alert>
                  ) : (
                    <FileUploadZone
                      onDrop={() => {}} // Handled internally by the component
                      onUploadComplete={handleUploadComplete}
                      multiple={true}
                      organizationId={currentOrganizationId || ''}
                      teamId={currentTeamId}
                      metadata={{
                        // Don't send description - let backend use filename as default
                        // Don't send content_type - let backend determine from MIME type
                        tag_group_values: {}
                      }}
                      affirmationStatus={affirmationStatus}
                      useAiTitle={useAiTitle}
                      useAiDescription={useAiDescription}
                      aiTagMode={aiTagMode}
                    />
                  )}

                  {uploadedFiles.length > 0 && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      {uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''} ready for metadata entry
                    </Alert>
                  )}
                </>
              )}
            </Box>
          )}

          {/* Step 2: File list with per-row and bulk metadata editing */}
          {activeStep === 1 && (
            <>
              <UploadFileList
                totalCount={uploadedFiles.length}
                selectedCount={selectedFileIds.size}
                allSelected={selectedFileIds.size === uploadedFiles.length && uploadedFiles.length > 0}
                someSelected={selectedFileIds.size > 0}
                onToggleAll={handleToggleSelectAll}
                onBulkSetStatus={(anchor) => { setStatusMenuAnchor(anchor); setStatusMenuTarget('bulk'); }}
                onBulkAddTags={(anchor) => { setTagPopoverAnchor(anchor); setTagPopoverTarget('bulk'); }}
                onBulkRemove={handleBulkRemove}
                onBulkClear={() => setSelectedFileIds(new Set())}
              >
                {uploadedFiles.map((file, idx) => {
                  const id = fileIdOf(idx);
                  return (
                    <UploadFileRow
                      key={id}
                      fileId={id}
                      fileName={file.fileName}
                      fileSize={file.totalBytes ?? 0}
                      fileType={resolveFileType(file)}
                      status={contentData[idx]?.status ?? 'draft'}
                      tags={contentData[idx]?.tags ?? []}
                      availableTags={availableTags}
                      aiTitle={aiDefaults.aiTitle}
                      aiDescription={aiDefaults.aiDescription}
                      title={contentData[idx]?.title}
                      description={contentData[idx]?.description}
                      selected={selectedFileIds.has(id)}
                      onSelectChange={() => handleToggleSelect(id)}
                      onStatusClick={(anchor) => { setStatusMenuAnchor(anchor); setStatusMenuTarget(idx); }}
                      onAddTagClick={(anchor) => { setTagPopoverAnchor(anchor); setTagPopoverTarget(idx); }}
                      onRemove={() => handleRowRemove(idx)}
                      errored={!aiDefaults.aiTitle && !(contentData[idx]?.title?.trim())}
                      onTitleChange={(next) => {
                        const existing = contentData[idx] ?? {};
                        handleBatchUpdate(idx, { ...existing, title: next });
                      }}
                      onDescriptionChange={(next) => {
                        const existing = contentData[idx] ?? {};
                        handleBatchUpdate(idx, { ...existing, description: next });
                      }}
                    />
                  );
                })}
              </UploadFileList>

              <StatusMenu
                anchorEl={statusMenuAnchor}
                onClose={() => { setStatusMenuAnchor(null); setStatusMenuTarget(null); }}
                onPick={(s) => {
                  if (statusMenuTarget === 'bulk') handleBulkSetStatus(s);
                  else if (typeof statusMenuTarget === 'number') handleRowStatusChange(statusMenuTarget, s);
                }}
              />
              <AddTagsPopover
                anchorEl={tagPopoverAnchor}
                onClose={() => { setTagPopoverAnchor(null); setTagPopoverTarget(null); }}
                onApply={(tagsToAdd) => {
                  if (tagPopoverTarget === 'bulk') handleBulkAddTags(tagsToAdd);
                  else if (typeof tagPopoverTarget === 'number') handleRowTagsAdd(tagPopoverTarget, tagsToAdd);
                }}
                heading={tagPopoverTarget === 'bulk' ? `Add tags to ${selectedFileIds.size} files` : 'Add tags'}
                showMergeNote
                availableTags={availableTags}
                organizationId={currentOrganizationId || ''}
                teamId={currentTeamId || ''}
                userId={user?.id || ''}
                onTagCreated={(tag) => setAvailableTags((prev) => [...prev, tag])}
              />
            </>
          )}
        </Box>

        {/* Card Footer (fixed): status + Save & finish */}
        <Box
          sx={{
            px: 3, py: 1.75,
            borderTop: 1, borderColor: 'divider',
            flexShrink: 0,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            bgcolor: 'background.paper',
          }}
        >
          <Typography
            variant="body2"
            color={isSaveBlocked ? 'error' : 'text.secondary'}
            sx={{ fontWeight: isSaveBlocked ? 600 : 400 }}
            id="upload-footer-status"
          >
            {activeStep === 0
              ? 'Choose files to begin'
              : isSaveBlocked
                ? `${rowsNeedingTitle.length} ${rowsNeedingTitle.length === 1 ? 'file needs' : 'files need'} a title before you can save.`
                : `${uploadedFiles.length} file${uploadedFiles.length !== 1 ? 's' : ''} · AI enrichment running asynchronously`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              disabled={activeStep === 0 || isSaveBlocked}
              onClick={handleBatchSaveAll}
            >
              Save &amp; finish
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Content Affirmation Dialog */}
      <Dialog
        open={showAffirmationDialog}
        onClose={() => !affirmingContent && setShowAffirmationDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Content Upload Responsibility Affirmation
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Before uploading content, you must affirm that you understand and accept responsibility for the content you upload.
          </DialogContentText>

          <Alert severity="info" sx={{ mb: 3 }}>
            {affirmationStatus?.reason === 'never_affirmed' && 'This is your first time uploading content.'}
            {affirmationStatus?.reason === 'expired' && 'Your previous affirmation has expired (>90 days old).'}
            {affirmationStatus?.reason === 'terms_updated' && 'Our Terms of Service have been updated since your last affirmation.'}
          </Alert>

          <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'background.default' }}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              By affirming, you confirm that:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="• All content you upload complies with our Terms of Service" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• You have the legal right to upload and share this content" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• The content respects intellectual property rights and does not infringe on copyrights, trademarks, or patents" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• The content follows all applicable laws and regulations" />
              </ListItem>
              <ListItem>
                <ListItemText primary="• You understand that you are legally responsible for the content you upload" />
              </ListItem>
            </List>
          </Paper>

          <Alert severity="success" sx={{ mb: 2 }}>
            Your affirmation will be valid for 90 days and applies to all content you upload during that period.
          </Alert>

          <Typography variant="body2" color="text.secondary">
            Please review our{' '}
            <MuiLink component={Link} to="/terms" target="_blank" rel="noopener">
              Terms of Service
            </MuiLink>
            {' '}before affirming.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setShowAffirmationDialog(false)}
            disabled={affirmingContent}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAffirmContent}
            variant="contained"
            disabled={affirmingContent}
            startIcon={affirmingContent ? <CircularProgress size={20} /> : null}
          >
            {affirmingContent ? 'Affirming...' : 'I Affirm and Accept Responsibility'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog for Missing Descriptions */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Missing Descriptions</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            The following {confirmDialog.filesWithMissingDescriptions.length} file{confirmDialog.filesWithMissingDescriptions.length > 1 ? 's' : ''} {confirmDialog.filesWithMissingDescriptions.length > 1 ? 'are' : 'is'} missing descriptions.
            While descriptions are optional, they help make your content more discoverable and understandable.
          </DialogContentText>
          <DialogContentText sx={{ mb: 2 }}>
            Would you like to save anyway, or go back to add descriptions?
          </DialogContentText>
          <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', p: 1 }}>
            <List dense>
              {confirmDialog.filesWithMissingDescriptions.slice(0, 5).map((file) => (
                <ListItem key={file.index}>
                  <ListItemText
                    primary={file.fileName}
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              ))}
              {confirmDialog.filesWithMissingDescriptions.length > 5 && (
                <ListItem>
                  <ListItemText
                    primary={`...and ${confirmDialog.filesWithMissingDescriptions.length - 5} more`}
                    primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic', color: 'text.secondary' }}
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
            color="primary"
          >
            Go Back to Edit
          </Button>
          <Button
            onClick={confirmDialog.onConfirm}
            variant="contained"
            color="primary"
            autoFocus
          >
            Save Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ContentUpload;